import { useState, useEffect } from 'react';
import './ClubMemberCard.css';
import { supabase } from '../../lib/supabaseClient.js';
import { useNavigate } from 'react-router-dom';

// 동아리원 목록을 카드 형태로 보여주는 컴포넌트
export default function ClubMemberCard({ clubId, memberRole, onMemberCountChange }) {
    const navigate = useNavigate();

    // 동아리원 목록 저장
    const [members, setMembers] = useState([]);

    // 모달에 표시할 선택된 동아리원 정보
    const [selectedMember, setSelectedMember] = useState(null);

    // 현재 로그인한 사용자 정보 저장
    const [loginUser, setLoginUser] = useState(null);

    // 컴포넌트 실행 시 로그인 사용자와 동아리원 목록 조회
    useEffect(() => {
        fetchLoginUser();
        fetchMembers();

        // 동아리원 목록을 3초마다 갱신
        const interval = setInterval(() => {
            fetchMembers();
        }, 3000);

        // 컴포넌트 종료 시 반복 실행 해제
        return () => clearInterval(interval);
    }, [clubId]);

    // 현재 로그인한 사용자 정보 조회
    async function fetchLoginUser() {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const { data: userData, error } = await supabase
            .from('users')
            .select('id')
            .eq('email', user.email)
            .single();

        if (error) {
            console.error('로그인 유저 조회 실패:', error.message);
            return;
        }

        setLoginUser(userData);
    }

    // 동아리원 목록 조회
    async function fetchMembers() {
        const { data, error } = await supabase
            .from('club_members')
            .select(`
                id,
                club_id,
                user_id,
                role,
                joined_at,
                user:user_id (
                    id,
                    name,
                    phone,
                    department,
                    student_number,
                    grade
                )
            `)
            .eq('club_id', clubId);

        if (error) {
            console.error('동아리원 조회 실패:', error.message);
            setMembers([]);
            onMemberCountChange?.(0);
            return;
        }

        // 동아리장을 먼저 표시
        setMembers(
            (data ?? []).sort((a, b) =>
                a.role === 'leader' ? -1 : b.role === 'leader' ? 1 : 0
            )
        );

        // 부모 컴포넌트에 동아리원 수 전달
        onMemberCountChange?.(data?.length ?? 0);
    }

    // 동아리원 퇴출 처리
    async function expelMember(member) {
        const confirmed = window.confirm(`${member.user?.name}님을 퇴출하시겠습니까?`);
        if (!confirmed) return;

        // club_members 테이블에서 해당 동아리원 삭제
        const { error } = await supabase
            .from('club_members')
            .delete()
            .eq('club_id', clubId)
            .eq('user_id', member.user_id);

        if (error) {
            alert('퇴출 실패');
            console.error('퇴출 실패:', error.message);
            return;
        }

        // 현재 회원 수 조회
        const { data: clubData } = await supabase
            .from('clubs')
            .select('member_count')
            .eq('id', clubId)
            .single();

        // 회원 수 감소
        await supabase
            .from('clubs')
            .update({
                member_count: Math.max((clubData?.member_count ?? 1) - 1, 0),
            })
            .eq('id', clubId);

        alert(`${member.user?.name}님이 퇴출되었습니다.`);
        setSelectedMember(null);
        fetchMembers();
    }

    async function delegateLeader(member) {
        if (!loginUser) {
            alert('로그인 정보를 불러오지 못했습니다.');
            return;
        }

        const confirmed = window.confirm(`${member.user?.name}님에게 동아리장을 위임하시겠습니까?`);
        if (!confirmed) return;

        // 1. 새 동아리장을 leader로 변경
        const { data: newLeaderData, error: newLeaderError } = await supabase
            .from('club_members')
            .update({ role: 'leader' })
            .eq('club_id', clubId)
            .eq('user_id', member.user_id)
            .select();

            console.log("새 동아리장 업데이트 결과:", newLeaderData);

        if (newLeaderError) {
            alert('새 동아리장 변경 실패');
            console.error('새 동아리장 변경 실패:', newLeaderError.message);
            return;
        }

        // 2. 기존 동아리장을 member로 변경
        const { error: oldLeaderError } = await supabase
            .from('club_members')
            .update({ role: 'member' })
            .eq('club_id', clubId)
            .eq('user_id', loginUser.id);

        if (oldLeaderError) {
            alert('기존 동아리장 변경 실패');
            console.error('기존 동아리장 변경 실패:', oldLeaderError.message);
            return;
        }

        // 3. clubs 테이블 leader_id 변경
        const { error: clubError } = await supabase
            .from('clubs')
            .update({ leader_id: member.user_id })
            .eq('id', clubId);

            if (clubError) {
            console.error("clubs leader_id 변경 실패:", clubError);
            alert(`동아리장 정보 변경 실패: ${clubError.message}`);
            return;
            }

        // 4. 위임 기록 저장
        const { error: transferError } = await supabase
            .from('leader_transfers')
            .insert({
                club_id: clubId,
                old_leader_id: loginUser.id,
                new_leader_id: member.user_id,
            });

        if (transferError) {
            console.error("위임 기록 저장 실패:", transferError);
            alert(`위임 기록 저장 실패: ${transferError.message}`);
            return;
            }

        alert(`${member.user?.name}님에게 동아리장이 위임되었습니다.`);
        setSelectedMember(null);
        fetchMembers();
        navigate(`/clubs/${clubId}`);
    }

    // 카드 클릭 시 동아리장만 상세 모달 열기
    function handleCardClick(member) {
        if (memberRole !== 'leader') return;
        setSelectedMember(member);
    }

    // 동아리원이 없을 때 안내 문구 표시
    if (members.length === 0) {
        return <div className="mc-empty">동아리원이 없습니다.</div>;
    }

    return (
        <>
            {/* 동아리원 카드 목록 */}
            <div className="mc-grid">
                {members.map((m) => (
                    <div
                        key={m.user_id}
                        className={`mc-card ${memberRole !== 'leader' ? 'disabled' : ''}`}
                        onClick={() => handleCardClick(m)}
                    >
                        {/* 동아리 내 역할 뱃지 */}
                        <span className={`mc-role-badge ${m.role === 'leader' ? 'leader' : 'member'}`}>
                            {m.role === 'leader' ? '동아리장' : '동아리원'}
                        </span>

                        <div className="mc-card-body">
                            {/* 이름 */}
                            <p className="mc-card-name">
                                {m.user?.name || '이름 없음'}
                            </p>

                            {/* 학과 */}
                            <p className="mc-card-sub">
                                {m.user?.department || '-'}
                            </p>

                            {/* 학년 */}
                            <div className="mc-card-footer">
                                <span className="mc-tag">
                                    {m.user?.grade ? `${m.user.grade}학년` : '-'}
                                </span>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* 동아리원 상세 정보 모달 */}
            {selectedMember && memberRole === 'leader' && (
                <div className="mc-modal-bg">
                    <div className="mc-modal">
                        <button
                            className="mc-modal-close"
                            onClick={() => setSelectedMember(null)}
                        >
                            ×
                        </button>

                        <h2 className="mc-modal-name">
                            {selectedMember.user?.name || '이름 없음'}
                        </h2>

                        {/* 동아리원 상세 정보 */}
                        <div className="mc-modal-fields">
                            <div className="mc-modal-field">
                                <span className="mc-modal-label">전화번호</span>
                                <span className="mc-modal-value">
                                    {selectedMember.user?.phone ?? '-'}
                                </span>
                            </div>

                            <div className="mc-modal-field">
                                <span className="mc-modal-label">학과</span>
                                <span className="mc-modal-value">
                                    {selectedMember.user?.department ?? '-'}
                                </span>
                            </div>

                            <div className="mc-modal-field">
                                <span className="mc-modal-label">학번</span>
                                <span className="mc-modal-value">
                                    {selectedMember.user?.student_number ?? '-'}
                                </span>
                            </div>

                            <div className="mc-modal-field">
                                <span className="mc-modal-label">학년</span>
                                <span className="mc-modal-value">
                                    {selectedMember.user?.grade
                                        ? `${selectedMember.user.grade}학년`
                                        : '-'}
                                </span>
                            </div>
                        </div>

                        {/* 본인이 아닌 동아리원에게만 관리 버튼 표시 */}
                        {selectedMember.user_id !== loginUser?.id && (
                            <div className="mc-modal-actions">
                                <button
                                    className="mc-action-btn expel"
                                    onClick={() => expelMember(selectedMember)}
                                >
                                    퇴출
                                </button>

                                <button
                                    className="mc-action-btn delegate"
                                    onClick={() => delegateLeader(selectedMember)}
                                >
                                    동아리장 위임
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}